import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuCheckboxItem, DropdownMenuContent, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Columns3, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";

/**
 * Reusable DataTable.
 * columns: [{ key, label, sortable?, hidden?, render?(row), className? }]
 * data: array of rows
 * rowActions: (row) => ReactNode
 */
export default function DataTable({ columns, data = [], loading, empty, rowActions, rowClassName, searchKeys = [], initialPageSize = 10, extraFilters, testid = "dt" }) {
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [visible, setVisible] = useState(() => Object.fromEntries(columns.map((c) => [c.key, !c.hidden])));
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  const filtered = useMemo(() => {
    let out = data;
    if (query && searchKeys.length) {
      const q = query.toLowerCase();
      out = out.filter((r) => searchKeys.some((k) => String(k.split(".").reduce((v, kk) => v?.[kk], r) ?? "").toLowerCase().includes(q)));
    }
    if (sortKey) {
      const dir = sortDir === "asc" ? 1 : -1;
      out = [...out].sort((a, b) => {
        const av = sortKey.split(".").reduce((v, k) => v?.[k], a);
        const bv = sortKey.split(".").reduce((v, k) => v?.[k], b);
        if (av == null) return 1;
        if (bv == null) return -1;
        return av > bv ? dir : av < bv ? -dir : 0;
      });
    }
    return out;
  }, [data, query, sortKey, sortDir, searchKeys]);

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortKey(k); setSortDir("asc"); }
  };

  const visibleCols = columns.filter((c) => visible[c.key]);

  return (
    <div data-testid={testid} className="data-card p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid={`${testid}-search`} value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} placeholder="Search…" className="pl-9 h-9" />
        </div>
        {extraFilters}
        <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
          <SelectTrigger className="w-28 h-9" data-testid={`${testid}-pagesize`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>)}
          </SelectContent>
        </Select>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" data-testid={`${testid}-cols`}><Columns3 className="w-4 h-4 mr-1" />Columns</Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {columns.map((c) => (
              <DropdownMenuCheckboxItem key={c.key} checked={!!visible[c.key]} onCheckedChange={(v) => setVisible((s) => ({ ...s, [c.key]: v }))}>
                {c.label}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {visibleCols.map((c) => (
                <TableHead key={c.key} className={c.className}>
                  {c.sortable ? (
                    <button data-testid={`${testid}-sort-${c.key}`} onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 hover:text-foreground">
                      {c.label}
                      {sortKey === c.key ? (sortDir === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                    </button>
                  ) : c.label}
                </TableHead>
              ))}
              {rowActions && <TableHead className="w-20 text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={visibleCols.length + (rowActions ? 1 : 0)} className="text-center text-muted-foreground py-8">Loading…</TableCell></TableRow>
            )}
            {!loading && pageRows.length === 0 && (
              <TableRow><TableCell colSpan={visibleCols.length + (rowActions ? 1 : 0)} className="text-center text-muted-foreground py-8">{empty || "No records"}</TableCell></TableRow>
            )}
            {!loading && pageRows.map((row, i) => (
              <TableRow key={row.id || i} data-testid={`${testid}-row-${row.id || i}`} className={typeof rowClassName === "function" ? rowClassName(row) : rowClassName}>
                {visibleCols.map((c) => (
                  <TableCell key={c.key} className={c.className}>
                    {c.render ? c.render(row) : String(c.key.split(".").reduce((v, k) => v?.[k], row) ?? "")}
                  </TableCell>
                ))}
                {rowActions && <TableCell className="text-right">{rowActions(row)}</TableCell>}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <div>Showing {total === 0 ? 0 : (page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}</div>
        <div className="flex items-center gap-2">
          <Button data-testid={`${testid}-prev`} variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}><ChevronLeft className="w-3 h-3" /></Button>
          <span className="number">Page {page} / {totalPages}</span>
          <Button data-testid={`${testid}-next`} variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages}><ChevronRight className="w-3 h-3" /></Button>
        </div>
      </div>
    </div>
  );
}
